import clsx from 'clsx';
import { useRouteMatch } from 'react-router-dom';
import { useRecoilState, useRecoilValue } from 'recoil';
import { useForm } from 'react-hook-form';
import { Button, Collapse, useMediaQuery, ListItem, ListItemText } from '@material-ui/core';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { type StandardDivProps } from '@sprinx/react-mui-components';
import { createStyles, makeStyles, Theme, useTheme } from '@material-ui/core/styles';
import Skeleton from '@material-ui/lab/Skeleton';
import { ExpandLess, ExpandMore } from '@material-ui/icons';
import { apiClientState } from '../../../api/appState';
import {
  getProductParameterTypesWithValueCounts,
  ProductParameterTypesWithValueCounts,
} from '../../../api/products/productParameterTypes';
import { catalogueFilterState } from '../../../api/products/catalogue';
import useTranslateWithFallback from '../../../hooks/useTranslateWithFallback';
import { CataloguePageMainFilterParam } from './CataloguePageMainFilterParam';

export type CataloguePageMainFilterClassKey =
  | 'root'
  | 'expandButton'
  | 'expandButtonLabel'
  | 'collapseContent'
  | 'resetButton';
export type CataloguePageMainFilterProps = StandardDivProps<CataloguePageMainFilterClassKey>;

const useStyles = makeStyles((theme: Theme) =>
  createStyles<CataloguePageMainFilterClassKey, {}>({
    root: {
      display: 'flex',
      height: 'inherit',
      flexDirection: 'column',
      padding: theme.spacing(2.5),
      backgroundColor: '#FBFBFB',
      fontSize: '1rem',
      '& > div:not(:first-child)': {
        borderTop: '1px solid #e8e8ed',
      },
    },
    expandButton: {
      border: `1px solid ${theme.palette.divider}`,
      borderRadius: theme.shape.borderRadius,
      marginBottom: '1rem',
    },
    expandButtonLabel: {
      fontWeight: theme.typography.fontWeightMedium,
    },
    collapseContent: {
      display: 'flex',
      flexDirection: 'column',
    },
    resetButton: {
      marginTop: '1rem',
    },
  }),
);

const CataloguePageMainFilter = React.forwardRef<HTMLDivElement, CataloguePageMainFilterProps>(
  ({ className, classes: pClasses, ...rest }, ref) => {
    const classes = useStyles({ classes: pClasses });
    const t = useTranslateWithFallback();
    const [filter, setFilter] = useRecoilState(catalogueFilterState);
    const [clicked, setClicked] = useState<NodeJS.Timeout | null>(null);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [hasError, setHasError] = useState<boolean>(false);
    const [enumParameterTypes, setEnumParameterTypes] = useState<ProductParameterTypesWithValueCounts[]>([]);
    const { url } = useRouteMatch();
    const taxonomy = url/* .slice(3) */ || '/catalogue';
    const apiClient = useRecoilValue(apiClientState);
    const theme = useTheme();
    const isLargeScreen = useMediaQuery(theme.breakpoints.up('lg'));
    const [expanded, setExpanded] = useState(isLargeScreen);

    useEffect(() => {
      setExpanded(isLargeScreen);
    }, [isLargeScreen]);

    const handleExpandClick = () => {
      setExpanded(!expanded);
    };

    const defaultValues = useMemo(
      () =>
        enumParameterTypes.reduce<Record<string, any>>((acc, x) => {
          acc[x.code] = [];
          return acc;
        }, {}),
      [enumParameterTypes],
    );

    const { handleSubmit, control, reset } = useForm({ defaultValues });

    const onSubmit = useCallback(
      (data: Record<string, any>) => {
        const validData = Object.entries(data).reduce<Record<string, any>>((acc, [key, value]) => {
          const correctedValue =
            value && Array.isArray(value) && value.filter((x) => x != null).length > 0 ? value : null;
          if (correctedValue && correctedValue.length > 0) {
            acc[key] = correctedValue;
          }
          return acc;
        }, {});

        setFilter((prev) => ({ ...prev, filterParams: validData }));
      },
      [setFilter],
    );

    const onChangeSubmit = useCallback(() => {
      handleSubmit(onSubmit)();
    }, [handleSubmit, onSubmit]);

    const handleReset = useCallback(() => {
      reset(defaultValues);
      onChangeSubmit();
    }, [reset, onChangeSubmit]);

    const handleConfirmed = useCallback(() => {
      if (clicked) {
        clearTimeout(clicked);
        handleReset();
        setClicked(null);
      } else {
        setClicked(null);
      }
    }, [handleReset, setClicked, clicked]);

    const handleButtonClick = useCallback(() => {
      if (!clicked) {
        setClicked(
          setTimeout(() => {
            setClicked(null);
          }, 4000),
        );
        return;
      }

      handleConfirmed();
    }, [clicked, handleConfirmed]);

    useEffect(() => {
      async function fetchData() {
        setIsLoading(true);
        try {
          const res = await getProductParameterTypesWithValueCounts(apiClient, taxonomy, taxonomy.includes('/catalogue/outlet'));
          setEnumParameterTypes(res.filter((x) => x.valueCounts.length > 0));
          setHasError(false);
        } catch (e) {
          console.error('Parameter Types loading error', e);
          setHasError(true);
        }
        setIsLoading(false);
      }
      handleReset();
      fetchData();
    }, [taxonomy, handleReset, setHasError, setIsLoading, setEnumParameterTypes]);

    if (isLoading) {
      return (
        <div ref={ref} className={clsx(classes.root, className)} {...rest}>
          <Skeleton variant='rect' height={'100%'} />
        </div>
      );
    }

    if (hasError) {
      return null;
    }

    if (enumParameterTypes.length === 0) {
      return (
        <div ref={ref} className={clsx(classes.root, className)} {...rest}>
          <p>{t('app/dataIsNotAvailable')}</p>
        </div>
      );
    }

    return (
      <div ref={ref} className={clsx(classes.root, className)} {...rest}>
        {!isLargeScreen && (
          <ListItem className={classes.expandButton} button onClick={handleExpandClick}>
            <ListItemText
              classes={{ primary: classes.expandButtonLabel }}
              primary={expanded ? t('common/hideFilter') : t('common/showFilter')}
            />
            {expanded ? <ExpandLess /> : <ExpandMore />}
          </ListItem>
        )}
        <Collapse classes={{ wrapperInner: classes.collapseContent }} in={expanded} timeout='auto' unmountOnExit>
          {Object.keys(filter.filterParams || {}).length > 0 && (
            <Button
              className={classes.resetButton}
              variant='outlined'
              color={clicked ? 'secondary' : 'default'}
              onClick={handleButtonClick}
            >
              {clicked ? t('common/confirmReset') : t('common/resetFilter')}
            </Button>
          )}
          <form onChange={onChangeSubmit}>
            {enumParameterTypes.map((type) => (
              <CataloguePageMainFilterParam
                key={type.code}
                control={control}
                param={type}
                onChangeSubmit={onChangeSubmit}
              />
            ))}
          </form>
        </Collapse>
      </div>
    );
  },
);

CataloguePageMainFilter.displayName = 'CataloguePageMainFilter';

export default CataloguePageMainFilter;
